import React from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
// import Pagination from "./Pagination";
import { fmtDate } from "../utils/inventory.helpers";

function SelectLike({
  value,
  onChange,
  placeholder,
  options,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selected = (options || []).find(
    (o) => String(o.value ?? o) === String(value)
  );

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`h-11 w-full rounded-xl border px-4 pr-10 text-left text-[13px] outline-none transition
          ${
            disabled
              ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
              : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
          }`}
      >
        <span className="block truncate">
          {selected ? selected.label ?? selected : placeholder}
        </span>
      </button>

      <ChevronDown
        size={18}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${
          open ? "rotate-180" : ""
        }`}
      />

      {open && !disabled && (
        <div className="absolute left-0 top-full z-[999] mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-[0_10px_30px_rgba(216,106,120,0.15)]">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full truncate px-4 py-3 text-left text-[13px] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>

          {(options || []).map((o) => {
            const val = o.value ?? o;
            const label = o.label ?? o;
            const active = String(value) === String(val);

            return (
              <button
                key={val}
                type="button"
                onClick={() => {
                  onChange(val);
                  setOpen(false);
                }}
                title={label}
                className={`w-full truncate px-4 py-3 text-left text-[13px] transition
                  ${
                    active
                      ? "bg-[#d86a78] text-white"
                      : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getVariantLabel(item = {}) {
  return (
    item?.variant_name ||
    item?.variantName ||
    item?.pack_label ||
    item?.packLabel ||
    [item?.weight_value || item?.weightValue, item?.weight_unit_symbol || item?.weightUnitSymbol]
      .filter(Boolean)
      .join(" ") ||
    (item?.is_default ? "Default" : "Variant")
  );
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toFixed(2)}`;
}

function getProductImage(item) {
  const img =
    item?.variant_image_url ||
    item?.variantImageUrl ||
    item?.image_url ||
    item?.imageUrl ||
    item?.image ||
    item?.details?.image_url ||
    item?.details?.imageUrl ||
    "";

  if (!img) return "";

  if (/^https?:\/\//i.test(img)) return img;

  const baseUrl = process.env.REACT_APP_CATALOG_BASE_URL || "";

  if (!baseUrl) return img;

  return `${baseUrl.replace(/\/$/, "")}/${img.replace(/^\//, "")}`;
}

function ProductThumb({ src, alt }) {
  const hasImage = Boolean(src);

  return (
    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-[#fff7f8]">
      {hasImage ? (
        <>
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fallback = e.currentTarget.nextSibling;
              if (fallback) fallback.style.display = "flex";
            }}
          />
          <div
            className="h-full w-full items-center justify-center text-[11px] font-semibold text-slate-400"
            style={{ display: "none" }}
          >
            N/A
          </div>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[11px] font-semibold text-slate-400">
          N/A
        </div>
      )}
    </div>
  );
}


function getStockRowKey(item = {}) {
  return `${item?.product_id || item?.productId || ""}:${item?.product_variant_id || item?.productVariantId || item?.variant_id || item?.variantId || ""}`;
}

function getStockDisplayStatus(item = {}) {
  return String(item?.stock_display_status || item?.stockDisplayStatus || "OUT_OF_STOCK").toUpperCase() === "COMING_SOON"
    ? "COMING_SOON"
    : "OUT_OF_STOCK";
}

function getStockDisplayStatusLabel(status) {
  return status === "COMING_SOON" ? "Coming Soon" : "Out of Stock";
}

export default function StockSection({
  stats,
  stockQuery,
  setStockQuery,
  includeBatches,
  setIncludeBatches,
  onlyLow,
  setOnlyLow,
  onlyComingSoon = false,
  setOnlyComingSoon,
  onUpdateStockDisplayStatus,
  stockDisplayStatusSavingKey = "",
  onMarkStockZero,
  stockZeroSavingKey = "",
  loadStock,
  loading,
  paginatedStock,
  stockPage,
  stockTotalPages,
  setStockPage,
  stockTotal,
  stockStartIndex,
  stockEndIndex,
  stockPageSize,
  stockPageSizeOptions,
  stockCanPrev,
  stockCanNext,
  onChangeStockPageSize,
  stockCategory,
  setStockCategory,
  stockSubcategory,
  setStockSubcategory,
  stockBrand,
  setStockBrand,
  stockFilterOptions,
  stockFilterLoading,
  stockFilterError,
  clearStockFilters,
}) {
  const navigate = useNavigate();
  const pendingPageRestoreRef = React.useRef(null);

  const restoreStockPageAfterStatusUpdate = React.useCallback((pageToRestore) => {
    const safePage = Number(pageToRestore);

    if (!Number.isFinite(safePage) || safePage <= 1 || typeof setStockPage !== "function") {
      return;
    }

    window.setTimeout(() => setStockPage(safePage), 0);
    window.setTimeout(() => setStockPage(safePage), 150);
  }, [setStockPage]);

  React.useEffect(() => {
    if (loading || !pendingPageRestoreRef.current) {
      return;
    }

    const pageToRestore = pendingPageRestoreRef.current;
    pendingPageRestoreRef.current = null;
    restoreStockPageAfterStatusUpdate(pageToRestore);
  }, [loading, restoreStockPageAfterStatusUpdate]);

  const handleUpdateStockDisplayStatus = React.useCallback((item, nextStatus) => {
    const pageToRestore = Number(stockPage) || 1;

    pendingPageRestoreRef.current = pageToRestore;

    const result = onUpdateStockDisplayStatus?.(item, nextStatus, {
      preservePage: true,
      stockPage: pageToRestore,
    });

    Promise.resolve(result).finally(() => {
      restoreStockPageAfterStatusUpdate(pageToRestore);
    });
  }, [onUpdateStockDisplayStatus, restoreStockPageAfterStatusUpdate, stockPage]);


  const handleMarkStockZero = React.useCallback((item) => {
    const availableQty = Number(item?.qty_available || item?.qtyAvailable || 0);
    const productLabel = item?.name || "this product";

    if (availableQty <= 0) {
      window.alert("This product variant already has zero available stock.");
      return;
    }

    const reason = window.prompt(
      `Reason for making ${productLabel} stock zero?`,
      "Vegetables spoiled / damaged"
    );

    const finalReason = String(reason || "").trim();
    if (!finalReason) return;

    if (finalReason.length < 3) {
      window.alert("Please enter a valid reason.");
      return;
    }

    const ok = window.confirm(
      `This will move ${availableQty} available unit(s) to damaged stock for this warehouse only. Continue?`
    );

    if (!ok) return;

    onMarkStockZero?.(item, finalReason);
  }, [onMarkStockZero]);

  const hasActiveFilters = Boolean(
    stockQuery?.trim?.() || stockCategory || stockSubcategory || stockBrand || onlyLow || onlyComingSoon,
  );

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
            Products
          </div>
          <div className="mt-3 text-[28px] font-bold text-slate-800">
            {stats.totalProducts}
          </div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
            Qty Available
          </div>
          <div className="mt-3 text-[28px] font-bold text-slate-800">
            {stats.totalAvailable}
          </div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
            Low Stock Items
          </div>
          <div className="mt-3 text-[28px] font-bold text-slate-800">
            {stats.lowItems}
          </div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
            Expiry Alerts
          </div>
          <div className="mt-3 text-[28px] font-bold text-slate-800">
            {stats.expiryItems}
          </div>
        </div>
      </div>

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative w-full xl:flex-1">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="Search stock by product or SKU"
                className="h-[44px] w-full rounded-[12px] border border-[#f2c6cc] pl-11 pr-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <label className="inline-flex h-[44px] items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] font-medium text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={includeBatches}
                  onChange={(e) => setIncludeBatches(e.target.checked)}
                />
                Include batches
              </label>

              <label className="inline-flex h-[44px] items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] font-medium text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={onlyLow}
                  onChange={(e) => setOnlyLow(e.target.checked)}
                />
                Only low stock
              </label>

              <label className="inline-flex h-[44px] items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] font-medium text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={onlyComingSoon}
                  onChange={(e) => setOnlyComingSoon?.(e.target.checked)}
                />
                Coming soon
              </label>
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
          <SelectLike
            value={stockCategory}
            onChange={(value) => {
              setStockCategory(value);
              setStockSubcategory("");
            }}
            placeholder="Any category level"
            options={stockFilterOptions?.category || []}
            disabled={stockFilterLoading}
          />

          <SelectLike
            value={stockBrand}
            onChange={setStockBrand}
            placeholder="Brand"
            options={stockFilterOptions?.brand || []}
            disabled={stockFilterLoading}
          />

          <button
            type="button"
            onClick={clearStockFilters}
            disabled={!hasActiveFilters}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] bg-white px-4 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={15} />
            Clear Filters
          </button>
        </div>

        {stockFilterError ? (
          <div className="mt-3 rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-[12px] font-medium text-[#d86a78]">
            {stockFilterError}
          </div>
        ) : null}
      </div>

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white shadow-[0_12px_30px_rgba(216,106,120,0.08)]">
        <div className="flex flex-col gap-4 border-b border-[#f8dde1] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-[17px] font-bold text-slate-800">
              Stock Summary
            </h3>

            {/* <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#fff3f5] px-3 py-1 text-[12px] font-bold text-[#d86a78]">
                Total Stock Items: {stockTotal}
              </span>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Showing {stockStartIndex}-{stockEndIndex}
              </span>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Page {stockPage} / {stockTotalPages}
              </span>
            </div> */}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className={`w-full table-fixed ${onlyComingSoon ? "min-w-[1500px]" : "min-w-[1640px]"}`}>
            <thead className="bg-[#fff7f8]">
              <tr className="text-left text-[13px] font-semibold text-slate-600">
                <th className="w-[30%] px-5 py-4">Product</th>
                <th className="w-[15%] px-5 py-4">SKU</th>
                <th className="w-[10%] px-5 py-4">MRP</th>
                <th className="w-[13%] px-5 py-4">Selling Price</th>
                <th className="w-[8%] px-5 py-4">Sold</th>
                <th className="w-[10%] px-5 py-4">Available</th>
                {!onlyComingSoon ? (
                  <>
                    <th className="w-[10%] px-5 py-4">Reserved</th>
                    <th className="w-[10%] px-5 py-4">Damaged</th>
                    <th className="w-[14%] px-5 py-4">Nearest Expiry</th>
                  </>
                ) : null}
                <th className="w-[12%] px-5 py-4">Expiring Batches</th>
                <th className="w-[14%] px-5 py-4">Emergency</th>
                {onlyComingSoon ? (
                  <>
                    <th className="w-[12%] px-5 py-4">User Status</th>
                    <th className="w-[14%] px-5 py-4">Action</th>
                  </>
                ) : null}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={onlyComingSoon ? 10 : 11}
                    className="px-5 py-8 text-center text-[14px] text-slate-500"
                  >
                    Loading stock...
                  </td>
                </tr>
              ) : paginatedStock.length ? (
                paginatedStock.map((item) => {
                  const productImage = getProductImage(item);

                  return (
                    <React.Fragment
                      key={`${item.product_id}-${item.product_variant_id || "default"}`}
                    >
                      <tr
                        className="cursor-pointer border-t border-[#f8dde1] align-top text-[14px] text-slate-700 transition hover:bg-[#fff8fa]"
                        onClick={() =>
                          item.product_id &&
                          navigate(`/admin/dashboard/inventory/${item.product_id}`, {
                            state: {
                              backTo:
                                window.location.pathname === "/admin/dashboard/stock"
                                  ? "/admin/dashboard/stock"
                                  : "/admin/dashboard/inventory",
                            },
                          })
                        }
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <ProductThumb
                              src={productImage}
                              alt={item.name || "Product"}
                            />

                            <div className="min-w-0">
                              <div
                                title={item.name}
                                className="max-w-[260px] truncate whitespace-nowrap font-semibold text-slate-800"
                              >
                                {item.name || "-"}
                              </div>

                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span
                                  title={getVariantLabel(item)}
                                  className="max-w-[220px] truncate rounded-full bg-[#fff3f5] px-2.5 py-1 text-[11px] font-semibold text-[#d86a78]"
                                >
                                  Variant: {getVariantLabel(item)}
                                </span>

                                {item?.is_default ? (
                                  <span className="rounded-full bg-[#eefbf3] px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                                    Default
                                  </span>
                                ) : null}
                              </div>

                              <div className="mt-1 flex flex-wrap gap-3 text-[12px] text-slate-500">
                                <span>SKU: {item.variant_sku || item.sku || "-"}</span>
                                {item.unit_symbol ? (
                                  <span>Unit: {item.unit_symbol}</span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4 align-middle truncate whitespace-nowrap">
                          {item.variant_sku || item.sku || "-"}
                        </td>

                        <td className="px-5 py-4 align-middle font-semibold text-slate-700">
                          {money(item.mrp)}
                        </td>

                        <td className="px-5 py-4 align-middle font-bold text-[#d86a78]">
                          {money(item.selling_price || item.sellingPrice)}
                        </td>

                        <td className="px-5 py-4 align-middle font-semibold text-slate-700">
                          {Number(item.sold_qty || item.soldQty || 0)}
                        </td>

                        <td className="px-5 py-4 align-middle">
                          {Number(item.qty_available || 0)}
                        </td>

                        {!onlyComingSoon ? (
                          <>
                            <td className="px-5 py-4 align-middle">
                              {Number(item.qty_reserved || 0)}
                            </td>

                            <td className="px-5 py-4 align-middle">
                              {Number(item.qty_damaged || 0)}
                            </td>

                            <td className="px-5 py-4 align-middle">
                              {fmtDate(item.nearest_expiry)}
                            </td>
                          </>
                        ) : null}

                        <td className="px-5 py-4 align-middle">
                          {Number(item.expiring_batches_count || 0)}
                        </td>

                        <td className="px-5 py-4 align-middle">
                          {(() => {
                            const availableQty = Number(item.qty_available || item.qtyAvailable || 0);
                            const key = getStockRowKey(item);
                            const saving = stockZeroSavingKey === key;
                            const manualReason = item.manual_out_of_stock_reason || item.manualOutOfStockReason || "";

                            return (
                              <div className="flex flex-col items-start gap-1">
                                <button
                                  type="button"
                                  disabled={saving || loading || availableQty <= 0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleMarkStockZero(item);
                                  }}
                                  className="inline-flex h-9 items-center justify-center rounded-[10px] border border-red-200 bg-red-50 px-3 text-[12px] font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {saving ? "Zeroing..." : "Stock Zero"}
                                </button>

                                {manualReason ? (
                                  <span
                                    title={manualReason}
                                    className="max-w-[140px] truncate text-[11px] font-semibold text-red-500"
                                  >
                                    {manualReason}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>

                        {onlyComingSoon ? (() => {
                          const status = getStockDisplayStatus(item);
                          const isComingSoon = status === "COMING_SOON";
                          const nextStatus = isComingSoon ? "OUT_OF_STOCK" : "COMING_SOON";
                          const saving = stockDisplayStatusSavingKey === getStockRowKey(item);

                          return (
                            <>
                              <td className="px-5 py-4 align-middle">
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-[12px] font-bold ${
                                    isComingSoon
                                      ? "bg-sky-50 text-sky-700"
                                      : "bg-rose-50 text-[#d86a78]"
                                  }`}
                                >
                                  {getStockDisplayStatusLabel(status)}
                                </span>
                              </td>

                              <td className="px-5 py-4 align-middle">
                                <button
                                  type="button"
                                  disabled={saving || loading}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleUpdateStockDisplayStatus(item, nextStatus);
                                  }}
                                  className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-bold text-[#d86a78] transition hover:bg-[#fff4f6] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {saving
                                    ? "Saving..."
                                    : isComingSoon
                                      ? "Mark Out of Stock"
                                      : "Mark Coming Soon"}
                                </button>
                              </td>
                            </>
                          );
                        })() : null}
                      </tr>

                      {includeBatches &&
                        Array.isArray(item.batches) &&
                        item.batches.length ? (
                        <tr className="border-t border-[#fdf0f2] bg-[#fffdfd]">
                          <td colSpan={onlyComingSoon ? 10 : 11} className="px-5 py-4">
                            <div className="rounded-[14px] border border-[#f7d8de] bg-[#fffafb] p-4">
                              <div className="mb-3 text-[13px] font-semibold text-slate-700">
                                Batch Details
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[720px]">
                                  <thead>
                                    <tr className="text-left text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                                      <th className="px-3 py-2">Batch No</th>
                                      <th className="px-3 py-2">MFG Date</th>
                                      <th className="px-3 py-2">Expiry Date</th>
                                      <th className="px-3 py-2">Available</th>
                                      <th className="px-3 py-2">Reserved</th>
                                      <th className="px-3 py-2">Damaged</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {item.batches.map((batch) => (
                                      <tr
                                        key={batch.id || batch.batch_id || batch.batchNo}
                                        className="border-t border-[#f7d8de] text-[13px] text-slate-700"
                                      >
                                        <td className="px-3 py-2">
                                          {batch.batch_no || batch.batchNo || "-"}
                                        </td>
                                        <td className="px-3 py-2">
                                          {fmtDate(batch.mfg_date || batch.mfgDate)}
                                        </td>
                                        <td className="px-3 py-2">
                                          {fmtDate(batch.expiry_date || batch.expiryDate)}
                                        </td>
                                        <td className="px-3 py-2">
                                          {Number(batch.qty_available || 0)}
                                        </td>
                                        <td className="px-3 py-2">
                                          {Number(batch.qty_reserved || 0)}
                                        </td>
                                        <td className="px-3 py-2">
                                          {Number(batch.qty_damaged || 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={onlyComingSoon ? 10 : 11}
                    className="px-5 py-8 text-center text-[14px] text-slate-500"
                  >
                    No stock found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f8dde1] px-5 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-center font-medium sm:text-left">
            Showing {stockStartIndex}-{stockEndIndex} of {stockTotal}
          </div>

          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center justify-between gap-3 sm:justify-start">
              <span>Rows</span>

              <select
                value={stockPageSize}
                disabled={loading}
                onChange={(e) => onChangeStockPageSize(e.target.value)}
                className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {stockPageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between gap-2 sm:justify-start">
              <button
                type="button"
                onClick={() => stockCanPrev && setStockPage((p) => p - 1)}
                disabled={!stockCanPrev || loading}
                className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>

              <span className="min-w-[86px] text-center font-medium">
                Page {stockPage} / {stockTotalPages}
              </span>

              <button
                type="button"
                onClick={() => stockCanNext && setStockPage((p) => p + 1)}
                disabled={!stockCanNext || loading}
                className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}